import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Plus, Edit2, Trash2, Tags, Percent, DollarSign, Truck
} from 'lucide-react';
import { useAdmin } from '../../context/AdminContext';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDiscounts = () => {
  const { getAuthHeaders } = useAdmin();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    value: '',
    min_order_value: '',
    max_uses: '',
    is_active: true
  });

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/discounts`, {
        headers: getAuthHeaders()
      });
      setDiscounts(response.data.discounts || []);
    } catch (error) {
      console.error('Failed to fetch discounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingDiscount(null);
    setFormData({
      code: '',
      discount_type: 'percentage',
      value: '',
      min_order_value: '',
      max_uses: '',
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (discount) => {
    setEditingDiscount(discount);
    setFormData({
      code: discount.code || '',
      discount_type: discount.discount_type || 'percentage',
      value: discount.value?.toString() || '',
      min_order_value: discount.min_order_value?.toString() || '',
      max_uses: discount.max_uses?.toString() || '',
      is_active: discount.is_active !== false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const discountData = {
      code: formData.code.toUpperCase(),
      discount_type: formData.discount_type,
      value: parseFloat(formData.value) || 0,
      min_order_value: formData.min_order_value ? parseFloat(formData.min_order_value) : null,
      max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
      is_active: formData.is_active
    };

    try {
      if (editingDiscount) {
        await axios.put(
          `${API}/admin/discounts/${editingDiscount._id}`,
          discountData,
          { headers: getAuthHeaders() }
        );
      } else {
        await axios.post(
          `${API}/admin/discounts`,
          discountData,
          { headers: getAuthHeaders() }
        );
      }
      setShowModal(false);
      fetchDiscounts();
    } catch (error) {
      console.error('Failed to save discount:', error);
    }
  };

  const deleteDiscount = async (discountId) => {
    if (!window.confirm('Are you sure you want to deactivate this discount?')) return;
    
    try {
      await axios.delete(`${API}/admin/discounts/${discountId}`, {
        headers: getAuthHeaders()
      });
      fetchDiscounts();
    } catch (error) {
      console.error('Failed to delete discount:', error);
    }
  };

  const getDiscountIcon = (type) => {
    switch (type) {
      case 'percentage':
        return <Percent className="h-5 w-5" />;
      case 'fixed_amount':
        return <DollarSign className="h-5 w-5" />;
      case 'free_shipping':
        return <Truck className="h-5 w-5" />;
      default:
        return <Tags className="h-5 w-5" />;
    }
  };

  const formatDiscountValue = (discount) => {
    switch (discount.discount_type) {
      case 'percentage':
        return `${discount.value}% OFF`;
      case 'fixed_amount':
        return `$${discount.value} OFF`;
      case 'free_shipping':
        return 'FREE SHIPPING';
      default:
        return discount.value;
    }
  };

  return (
    <AdminLayout>
      <div className="p-6" data-testid="admin-discounts-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-black text-stone-900">Discounts</h1>
            <p className="text-stone-500">Manage promotional codes and discounts</p>
          </div>
          <Button onClick={openCreateModal} className="gap-2 bg-amber-600 hover:bg-amber-700" data-testid="add-discount-btn">
            <Plus className="h-4 w-4" />
            Create Discount
          </Button>
        </div>

        {/* Discounts Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
          </div>
        ) : discounts.length === 0 ? (
          <div className="text-center py-12 text-stone-500">
            <Tags className="h-12 w-12 mx-auto mb-4 text-stone-300" />
            <p>No discount codes yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="discounts-grid">
            {discounts.map((discount) => (
              <Card 
                key={discount._id} 
                className={`border-0 shadow-lg ${!discount.is_active ? 'opacity-50' : ''}`}
                data-testid={`discount-card-${discount._id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      discount.discount_type === 'percentage' 
                        ? 'bg-blue-100 text-blue-600'
                        : discount.discount_type === 'fixed_amount'
                          ? 'bg-green-100 text-green-600'
                          : 'bg-purple-100 text-purple-600'
                    }`}>
                      {getDiscountIcon(discount.discount_type)}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditModal(discount)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-600"
                        onClick={() => deleteDiscount(discount._id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <p className="font-mono text-lg font-bold text-stone-900 bg-stone-100 px-3 py-2 rounded">
                      {discount.code}
                    </p>
                  </div>
                  
                  <p className="text-2xl font-black text-amber-600 mb-2">
                    {formatDiscountValue(discount)}
                  </p>
                  
                  <div className="space-y-1 text-sm text-stone-500">
                    {discount.min_order_value && (
                      <p>Min. order: ${discount.min_order_value}</p>
                    )}
                    {discount.max_uses && (
                      <p>Uses: {discount.uses_count || 0} / {discount.max_uses}</p>
                    )}
                    <p className={discount.is_active ? 'text-green-600' : 'text-red-600'}>
                      {discount.is_active ? '● Active' : '○ Inactive'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Discount Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingDiscount ? 'Edit Discount' : 'Create Discount'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Discount Code</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder="SAVE20"
                  required
                  disabled={!!editingDiscount}
                  className="font-mono"
                  data-testid="discount-code-input"
                />
              </div>
              
              <div>
                <Label>Discount Type</Label>
                <Select 
                  value={formData.discount_type} 
                  onValueChange={(v) => setFormData({...formData, discount_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage Off</SelectItem>
                    <SelectItem value="fixed_amount">Fixed Amount Off</SelectItem>
                    <SelectItem value="free_shipping">Free Shipping</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.discount_type !== 'free_shipping' && (
                <div>
                  <Label>
                    {formData.discount_type === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}
                  </Label>
                  <Input
                    type="number"
                    value={formData.value}
                    onChange={(e) => setFormData({...formData, value: e.target.value})}
                    placeholder={formData.discount_type === 'percentage' ? '20' : '10.00'}
                    required
                  />
                </div>
              )}

              <div>
                <Label>Minimum Order Value ($)</Label>
                <Input
                  type="number"
                  value={formData.min_order_value}
                  onChange={(e) => setFormData({...formData, min_order_value: e.target.value})}
                  placeholder="50.00 (optional)"
                />
              </div>

              <div>
                <Label>Maximum Uses</Label>
                <Input
                  type="number"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({...formData, max_uses: e.target.value})}
                  placeholder="100 (optional, leave empty for unlimited)"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Active</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                  {editingDiscount ? 'Update' : 'Create'} Discount
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminDiscounts;
